<template>
  <div class="kanban-board">
    <div
      class="kanban-list"
      v-for="column in Object.values(columnKanbanPolicies())"
      :key="column"
      :data-list-id="column"
      :class="{
        'kanban-list-status-orange': column === 1,
        'kanban-list-status-orange-66': column === 2,
        'kanban-list-status-orange-33': column === 15,
        'kanban-list-status-yellow': column === 18,
        'kanban-list-status-green': column === 16,
        'kanban-list-status-red': column === 9,
        'kanban-list-over': overColumn === column
      }"
      @dragenter.prevent="overColumn = column"
      @dragleave.prevent="overColumn = null"
      @drop.prevent="overColumn = null"
    >
      <div class="kanban-list-header">
        <div class="kanban-list-title">{{ getNameColumn(column) }}</div>
        <div
          class="kanban-list-total-cards"
          :title="`Total: ${cardCount(column)}`"
        >
          <i class="fa fa-copy"></i> {{ cardCount(column) }}
        </div>
      </div>

      <div
        class="kanban-list-cards"
        :class="{ 'is-loading': loadingColumns[column] }"
        @scroll="onScroll(column)"
        :ref="`kanbanListCards${column}`"
      >
        <!-- Apólices -->
        <draggable
          :list="localItemsPolicies[column] || []"
          :group="{ name: 'kanban', pull: true, put: true }"
          ghost-class="dragging-shadow"
          @start="onCardGet($event, 1)"
          @end="onCardDrop($event)"
          :move="onMoveCard"
          :data-list-id="column"
        >
          <div
            v-for="policy in localItemsPolicies[column] || []"
            :key="policy.id"
            class="kanban-card"
            :data-card-id="policy.id"
          >
            <div class="kanban-card-data">
              <div class="tomador smart-clip" :title="policy.policy_holder_name">
                {{ truncateText(policy.policy_holder_name) }}
              </div>
              <div class="smart-clip" :title="policy.number">
                <i class="mdi mdi-folder-open fa fa-fw mr-1"></i>
                Apólice nº {{ policy.number }}
              </div>
              <div class="smart-clip" :title="policy.insured_name">
                <i class="far fa-user mr-1"></i>
                {{ truncateText(policy.insured_name) }}
              </div>
              <div
                class="pill-label-kanban"
                :class="getClassTextColor(policy.deadline)"
                v-b-tooltip.hover
                :title="(policy.deadline > 0 ? 'Vence em ' : 'Venceu há ') + Math.abs(policy.deadline) + ' dia(s)'"
              >
                <span class="label"><i class="fas fa-calendar-alt mr-1"></i></span>
                <span class="value">{{ policy.validity_expiration }}</span>
              </div>
            </div>
            <div
              class="status-bar"
              :style="{ backgroundColor: cardColorsTags[getRealNameColumn(column)] }"
            ></div>
            <div class="kanban-card-actions">
              <b-dropdown
                variant="link"
                size="sm"
                no-caret
                left
                class="b-dropdown-custom-color"
              >
                <template #button-content>
                  <i class="fa fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item
                  v-if="$can.any(['policy-edit','policy-update']) &&
                    hasInsuranceCategoryIntegration(policy.insurance_category_id)"
                  @click="showModalCreate(policy)"
                >
                  <i class="fa fa-sync mr-2"></i> Renovar apólice
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isMaster()"
                  @click="showModalAutomaticRenewal(policy)"
                >
                  <i class="fa fa-clock mr-2"></i> Renovação automática
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can.any(['policy-edit','policy-update'])"
                  @click="showModalStatus(policy, column, true, true)"
                >
                  <i class="fa fa-pen mr-2"></i> Mais informação
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </draggable>

        <!-- Renovações -->
        <draggable
          :list="localItemsPolicyRenovation[column] || []"
          :group="{ name: 'kanban', pull: true, put: true }"
          ghost-class="dragging-shadow"
          @start="onCardGet($event, 2)"
          @end="onCardDrop($event)"
          :move="onMoveCard"
          :data-list-id="column"
        >
          <div
            v-for="ren in localItemsPolicyRenovation[column] || []"
            :key="ren.id"
            class="kanban-card"
            :data-card-id="ren.id"
          >
            <div class="kanban-card-data">
              <div
                class="tomador smart-clip"
                :title="ren.policy_origin?.policy_holder_name"
              >
                {{ truncateText(ren.policy_origin?.policy_holder_name || ren.insured_name) }}
              </div>
              <div class="smart-clip" :title="ren.policy_origin?.number">
                <i class="mdi mdi-folder-open fa fa-fw"></i>
                Apólice nº {{ ren.policy_origin?.number }}
              </div>
              <div
                class="smart-clip"
                :title="ren.quotation?.number || ren.quotation_number"
              >
                <i class="mdi mdi-buffer fa fa-fw mr-1"></i>
                Cotação nº {{ ren.quotation?.number || ren.quotation_number }}
              </div>
              <div
                class="smart-clip"
                :title="ren.policy_origin?.insured_name || ren.insured_name"
              >
                <i class="far fa-user mr-1"></i>
                {{ truncateText(ren.policy_origin?.insured_name || ren.insured_name) }}
              </div>
            </div>
            <div
              class="status-bar"
              :style="{ backgroundColor: cardColorsTags[getRealNameColumn(column)] }"
            ></div>
            <div class="kanban-card-actions">
              <b-dropdown
                variant="link"
                size="sm"
                no-caret
                class="text-secondary"
              >
                <template #button-content>
                  <i class="fa fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item
                  v-if="$can.any(['policy-edit','policy-update'])"
                  @click="showModalStatus(ren, column, true)"
                >
                  <i class="fa fa-pen mr-2"></i> Editar
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="ren.policy_renovation_files?.length"
                  @click="downloadAllFile(ren)"
                >
                  <i class="fas fa-paperclip"></i> Download documentos
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </draggable>

        <div
          v-if="loadingColumns[column]"
          class="overlay-column d-flex align-items-center justify-content-center"
        >
          <b-spinner small label="Carregando..."></b-spinner>
        </div>
      </div>
    </div>

    <!-- Modais -->
    <b-modal
      id="modal-renovation-status"
      ref="modalStatus"
      size="lg"
      @hide="closeModalAndMoveBack"
    >
      <template #modal-title>
        <h5 class="modal-title">Alterar Renovação</h5>
        <p class="text-muted mb-0">Revise e altere o status da renovação</p>
      </template>
      <ModalStatusRenewal
        :policy-renew-selected="policyRenewSelected"
        :editing="editing"
        :url-upload="urlUpload"
        :files="files"
        @close-modal-status="closeModalStatus"
        @disable-status-button="disableStatusButton"
        ref="modalStatusRenewal"
      />
      <template #modal-footer>
        <button
          type="button"
          class="btn btn-primary"
          @click="editing ? changeStatusPolicyRenovation() : $refs.modalStatus.hide()"
          :disabled="!canChangeStatus || loading"
        >
          <b-spinner v-if="loading" small></b-spinner>
          {{ editing ? 'Alterar' : 'Fechar' }}
        </button>
      </template>
    </b-modal>

    <b-modal
      id="show-modal-create"
      ref="modalCreate"
      size="md"
      hide-footer
      centered
      @hide="closeModalAndMoveBack"
    >
      <template #modal-title>Renovar apólice</template>
      <ManualPolicyRenewal :policy-renew-selected="policyRenewSelected" />
    </b-modal>

    <b-modal
      id="show-modal-automatic-renewal"
      ref="modalAutomaticRenewal"
      size="lg"
      hide-footer
    >
      <template #modal-title>Renovar apólice</template>
      <AutomaticPolicyRenewal :policy-id="policyIdSelected" />
    </b-modal>
  </div>
</template>


<script>
import draggable from 'vuedraggable';
import policyService from "@/services/policy";
import policyRenovationService from "@/services/policy-renovation";
import dateFormatter from "@/mixins/dateFormatter";
import commomsHelper from "@/mixins/commomsHelper";
import numericFormatter from "@/mixins/numericFormatter";
import authenticationService from "@/services/authentication";
import statusQuotationTypes from "@/enums/statusQuotationTypes";
import translateStatusQuotationTypes from "@/enums/translate/statusQuotationTypes";
import AutomaticPolicyRenewal from '@/components/Policy/Renewal/AutomaticPolicyRenewal.vue';
import ManualPolicyRenewal from '@/components/Policy/Renewal/ManualPolicyRenewal.vue';
import ModalStatusRenewal from '@/components/Policy/Renewal/ModalStatusRenewal.vue';
import fileDownload from "js-file-download";
import Vue from 'vue'

export default {
  name: "list-kanban-policy-renewal",
  components: {
    draggable,
    AutomaticPolicyRenewal,
    ManualPolicyRenewal,
    ModalStatusRenewal
  },
  mixins: [dateFormatter, commomsHelper, numericFormatter],
  props: {
    itemsPolicies: {},
    itemsPolicyRenovation: {},
    pages: [],
  },
  data() {
    return {
      loading: false,
      loading_download: false,
      translateStatusQuotationTypes: translateStatusQuotationTypes,
      statusQuotationTypes: statusQuotationTypes,
      cardColors: {
        UPCOMING: "kanban-list-status-orange",
        CONTACT: "kanban-list-status-orange-66",
        AWAITING: "kanban-list-status-orange-33",
        AWAITING_APPROVAL: "kanban-list-status-yellow",
        FINISHED: "kanban-list-status-green",
        CANCELED: "kanban-list-status-red",
      },
      policyRenewSelected: {
        policy_origin: {
          insured_name: null,
          policy_holder_document: null,
          policy_holder_name: null,
          number: null,
        },
      },
      loadingColumns: {
        1: false,
        2: false,
        15: false,
        16: false,
        18: false,
        9: false,
      },
      editing: false,
      files: [],
      urlUpload: '',
      cardColorsTags: {
        UPCOMING: "#ea712d",
        CONTACT: "#f18830",
        AWAITING: "#f89f32",
        AWAITING_APPROVAL: "#ffb635",
        AWAITING_DOCUMENTATION: "#ffb635",
        FINISHED: "#20c997",
        CANCELED: "crimson",
      },
      policyIdSelected: null,
      movedPolicy: null,
      fromColumn: null,
      toColumn: null,
      originalIndex: null,
      disable_status_button: true,
      selectedPolicy: {},
      status_id: null,
      cancellation_reason_id: null,
      overColumn: null,
      localItemsPolicies: [...(this.itemsPolicies || [])],
      localItemsPolicyRenovation: [...(this.itemsPolicyRenovation || [])],
      insuranceCategoriesWithIntegrations: [],
    }
  },
  computed: {
    cardCount() {
      return (column) => {
        var policyItems = this.localItemsPolicies[column];
        if (!this.localItemsPolicyRenovation[column]) {
          this.$set(this.localItemsPolicyRenovation, column, []);
        }

        var renovationItems = this.localItemsPolicyRenovation[column];

        if (policyItems) {
          return policyItems.length;
        }
        if (renovationItems) {
          return renovationItems.length;
        }
        return 0;
      };
    },
    canChangeStatus() {
      const st  = this.policyRenewSelected.status_id;
      const cr  = this.policyRenewSelected.cancellation_reason_id;
      // sem status escolhido
      if (!st)               return false;
      // sem motivo, se cancelado (9)
      if (st === 9 && !cr)   return false;
      // bloqueio especial para 1 e 2
      if (st === 1 || st === 2) return false;
      return true;
    }
  },
  watch: {
    status_id(val) {
      Vue.set(this.policyRenewSelected, 'status_id', val)
    },
    cancellation_reason_id(val) {
      Vue.set(this.policyRenewSelected, 'cancellation_reason_id', val)
    },
    itemsPolicies(newVal) {
      this.localItemsPolicies = [...newVal]
    },
    itemsPolicyRenovation(newVal) {
      this.localItemsPolicyRenovation = [...newVal]
    }
  },
  created() {
    this.$emit('update-kanban');
    this.getInsuranceCategoriesWithIntegrations();
  },
  methods: {
    onScroll(column) {
      const container = this.$refs[`kanbanListCards${column}`][0];
      if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
        this.addMoreResults(column);
      }
    },
    redirect(routeName, params = null) {
      this.$router.push({ name: routeName, params: params });
    },
    isMaster() {
      return authenticationService.isMaster();
    },
    columnKanbanPolicies() {
      return {
        UPCOMING: 1,
        CONTACT: 2,
        AWAITING: 15,
        AWAITING_APPROVAL: 18,
        FINISHED: 16,
        CANCELED: 9,
      };
    },
    addMoreResults(column) {
      if (this.pages[column].current_page < this.pages[column].last_page && !this.loadingColumns[column]) {
        this.loadingColumns[column] = true;
        var nextPage = this.pages[column].current_page + 1;
        const policyDueParams = {
          1: [120, 60],
          2: [60, -5]
        };

        if (policyDueParams[column]) {
          this.$emit('get-policy-due', ...policyDueParams[column], nextPage);
        } else if ([15, 18, 9, 16].includes(column)) {
          this.$emit('get-policies-renovations', column, nextPage);
        }
      }
    },
    showModalCreate(policy) {
        this.policyRenewSelected = policy;
        this.$refs.modalCreate.show();
    },
    showModalAutomaticRenewal(policy) {
      this.policyIdSelected = policy.id;
      this.$refs.modalAutomaticRenewal.show();
    },
    showModalStatus(policy, column, isClick = false, isNoRenovation = false) {
      const safePolicy = { ...policy };

      if (isNoRenovation) {
        delete safePolicy.policy_origin;
        safePolicy.policy_number     = policy.number;
        safePolicy.policy_origin_id  = policy.id;
        safePolicy.policy_origin     = { ...policy };
      }

      if (column !== 9) {
        safePolicy.cancellation_reason_id = null;
      }

      safePolicy.status_id = isClick ? column : this.toColumn;

      this.policyRenewSelected = {
        status_id: null,
        cancellation_reason_id: null,
        ...safePolicy
      };

      this.editing = [1, 2, 15, 18].includes(column);

      this.urlUpload = `/api/v1/policy-renovations/${this.policyRenewSelected.id}/files`;

      this.$refs.modalStatus.show();
    },
    changeStatusPolicyRenovation() {
      this.loading = true;
      this.resetAlert();
      policyRenovationService
        .updateStatusPolicyRenovation(this.policyRenewSelected.id, this.policyRenewSelected)
        .then((response) => {
          if (response.data.error) {
            this.$emit('show-error', response.data.message);
            return;
          }
          this.urlUpload = `/api/v1/policy-renovations/${response.data.id}/files`;
        })
        .catch(() => {
          this.$emit('show-error', "Não foi possível mudar o status da renovação.");
        })
        .finally(() => {
          this.loading = false;
          this.$refs.modalStatusRenewal.uploadFiles();
          this.$emit('update-kanban');
        });
    },
    destroyPolicyRenovation() {
      this.loading_delete = true;
      this.$refs.showModal.hide();
      policyRenovationService
        .destroyPolicyRenovation(this.policyRenewSelected.id)
        .then((response) => {
          if (response.data.error) {
            this.$emit('show-error', response.data.message);
            return;
          }
        })
        .catch(() => {
          this.$emit('show-error', 'Não foi possível remover a renovação.');
        })
        .finally(() => {
          this.policyRenewSelected = {};
          this.loading_delete = false;
        });
    },
    downloadAllFile(policyRenovation) {
      this.loading_download = true;
      policyRenovationService
        .getPolicyRenovationAllFiles(policyRenovation.id)
        .then((response) => {
          if (response.data.error) {
            this.$emit('show-error', response.data.message);
            return;
          }
          fileDownload(response.data, 'download.zip');
        })
        .catch(() => {
          this.$emit('show-error', 'Não foi possível realizar o download deste arquivo.');
        })
        .finally(() => {
          this.loading_download = false;
        });
    },
    getNameColumn(column) {
      switch (column) {
        case 1:
          return 'Renovação próxima';
        case 2:
          return 'Aguardando contato';
        case 15:
          return 'Aguardando documentação';
        default:
          return this.getEnumKeyByEnumValue(this.translateStatusQuotationTypes, column);
      }
    },
    getRealNameColumn(column) {
      switch (column) {
        case 1:
          return 'UPCOMING';
        case 2:
          return 'CONTACT';
        default:
          return this.getEnumKeyByEnumValue(this.statusQuotationTypes, column);
      }
    },
    getClassTextColor(days) {
      if (days < 7) {
        return 'pill-label-danger';
      } else if (days < 15) {
        return 'pill-label-warning';
      }
    },
    onCardDrop(event) {
      let toColumn = event.to.getAttribute('data-list-id');
      if (!this.canDropToColumn()) {
        return;
      }

      if ((this.fromColumn == 1 || this.fromColumn == 2) && toColumn == 15) {
        this.showModalCreate(this.movedPolicy);
      }

      if (
        (this.fromColumn === "9" && ["15", "16", "18"].includes(toColumn)) ||
        (this.fromColumn === "15" && ["9", "16", "18"].includes(toColumn)) ||
        (this.fromColumn === "16" && ["9", "15", "18"].includes(toColumn)) ||
        (this.fromColumn === "18" && ["9", "15", "16"].includes(toColumn))
      ) {
        this.showModalStatus(this.movedPolicyRenovation, this.fromColumn, false);
      }
    },
     onCardGet(event, status) {
        this.fromColumn = event.from.getAttribute('data-list-id');
        this.originalIndex = event.oldIndex;

        if (status == 1) {
          this.movedPolicy = this.localItemsPolicies[this.fromColumn][event.oldIndex] ?? null;
          this.movedPolicy.policy_origin = {
            id: this.movedPolicy.id,
            number: this.movedPolicy.number,
            policy_holder_document: this.movedPolicy.policy_holder_document,
            policy_holder_name: this.movedPolicy.policy_holder_name,
            validity_expiration: this.movedPolicy.validity_expiration,
          };
          this.localItemsPolicies[this.fromColumn][event.oldIndex].policy_origin = this.movedPolicy.policy_origin;
        }

        if (status == 2) {
          this.movedPolicyRenovation = this.localItemsPolicyRenovation[this.fromColumn][event.oldIndex] ?? null;
          this.movedPolicyRenovation.number = this.movedPolicyRenovation.policy_origin.number;
          this.movedPolicyRenovation.policy_holder_document = this.movedPolicyRenovation.policy_origin.policy_holder_document;
          this.movedPolicyRenovation.policy_holder_name = this.movedPolicyRenovation.policy_origin.policy_holder_name;
          this.movedPolicyRenovation.validity_expiration = this.movedPolicyRenovation.policy_origin.validity_expiration;

          this.localItemsPolicyRenovation[this.fromColumn][event.oldIndex] = this.movedPolicyRenovation;
        }
      },
    truncateText(text, maxChar = 22) {
      return text.length > maxChar ? `${text.substring(0, maxChar)}...` : text;
    },
    setloadingColum(column) {
        this.loadingColumns[column] = false;
    },
    canDropToColumn() {
      if (this.fromColumn == 1 && (this.toColumn == 15 || this.toColumn == 1)) {
        return true;
      }
      if (this.fromColumn == 2 && (this.toColumn == 15 || this.toColumn == 2)) {
        return true;
      }
      if (this.fromColumn == 15 && (this.toColumn == 15 || this.toColumn == 18 || this.toColumn == 16 || this.toColumn == 9)) {
        return true;
      }
      if (this.fromColumn == 18 && (this.toColumn == 18 || this.toColumn == 16 || this.toColumn == 9)) {
        return true;
      }
      return false;
    },
    onMoveCard(evt) {
      const isNoDrag = evt.dragged && evt.dragged.classList.contains('kanban-bottom-space');
      if (isNoDrag) {
        return false;
      }
      this.toColumn = evt.to.dataset.listId;
      if (!this.canDropToColumn()) {
        evt.from.classList.add('invalid-move');
        setTimeout(() => evt.from.classList.remove('invalid-move'), 1000);
        return false;
      }
      return true;
    },
    closeModalAndMoveBack() {
    if (this.fromColumn == 1 || this.fromColumn == 2) {
        if (this.localItemsPolicies[this.fromColumn]) {
          this.localItemsPolicies[this.fromColumn].splice(this.originalIndex, 0, this.movedPolicy);
        }

        if (this.localItemsPolicyRenovation[this.toColumn]) {
          const toColumnIndex = this.localItemsPolicyRenovation[this.toColumn].indexOf(this.movedPolicy);

          if (toColumnIndex !== -1) {
            this.localItemsPolicyRenovation[this.toColumn].splice(toColumnIndex, 1);
          }
        }
      }

    if (this.fromColumn != 1 && this.fromColumn != 2) {
        if (this.localItemsPolicyRenovation[this.fromColumn]) {
          this.localItemsPolicyRenovation[this.fromColumn].splice(this.originalIndex, 0, this.movedPolicyRenovation);
        }

        if (this.localItemsPolicyRenovation[this.toColumn]) {
          const toColumnIndex = this.localItemsPolicyRenovation[this.toColumn].indexOf(this.movedPolicyRenovation);
          if (toColumnIndex !== -1) {
            this.localItemsPolicyRenovation[this.toColumn].splice(toColumnIndex, 1);
          }
        }
      }

      this.movedPolicy = null;
      this.movedPolicyRenovation = null;
      this.fromColumn = null;
      this.toColumn = null;

      this.$refs.modalCreate.hide();
      this.$refs.modalStatus.hide();
    },
    closeModalStatus() {
      this.$refs.modalStatus.hide();
      this.$emit('update-kanban');
    },
    disableStatusButton(value) {
       this.disable_status_button = value;
    },
    showCancelRenovationModal(policy) {
      this.selectedPolicy = policy;
      this.$bvModal.show('modal-cancelar-renovacao');
    },
    
    getInsuranceCategoriesWithIntegrations() {
      policyService
        .getAllInsuranceCategoriesWithIntegrations()
        .then((response) => {
          this.insuranceCategoriesWithIntegrations = response.data;
        })
        .catch(() => {
          this.showError("Não foi possível recuperar a lista de integrações.");
        });
    },
    hasInsuranceCategoryIntegration(insuranceCategoryId) {
      return this.insuranceCategoriesWithIntegrations.some(
        item => item.id === insuranceCategoryId
      );
    },
  },
};
</script>


<style scoped>
.kanban-list-over {
  /* destaque igual ao antigo */
  box-shadow: inset 0 0 0 3px rgba(0,123,255,0.5);
}
</style>
